import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  AreaChart, Area, ComposedChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import { useSiteActions, actionMarkers, groupMarkersByDate, ActionTooltipRows, QuickActionModal } from './SiteActionsOverlay';
import adsApi from '../services/google-ads';
import concessionsApi from '../services/concessions';
import GoogleAdsChat from './GoogleAdsChat';
import SeaCampaignBuilder from './SeaCampaignBuilder';
import './GoogleAds.css';

// ── Utils dates ────────────────────────────────────────────────────────────────

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function computeRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const back = (n) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  switch (preset) {
    case '7j':   return { start: toISODate(back(6)),  end: toISODate(today) };
    case '28j':  return { start: toISODate(back(27)), end: toISODate(today) };
    case '90j':  return { start: toISODate(back(89)), end: toISODate(today) };
    case 'mois-en-cours': {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: toISODate(d), end: toISODate(today) };
    }
    case 'mois-dernier': {
      const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const f = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: toISODate(d), end: toISODate(f) };
    }
    case '12m': {
      const d = new Date(today.getFullYear(), today.getMonth() - 11, 1);
      return { start: toISODate(d), end: toISODate(today) };
    }
    default: return { start: toISODate(back(27)), end: toISODate(today) };
  }
}

const PRESETS = [
  { id: '7j',            label: '7 jours' },
  { id: '28j',           label: '28 jours' },
  { id: '90j',           label: '90 jours' },
  { id: 'mois-en-cours', label: 'Mois en cours' },
  { id: 'mois-dernier',  label: 'Mois dernier' },
  { id: '12m',           label: '12 mois' },
  { id: 'custom',        label: 'Personnalisé' },
];

// ── Utils format ───────────────────────────────────────────────────────────────

const nf = new Intl.NumberFormat('fr-FR');
const nf1 = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });
const nf2 = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });

function fmtNumber(n) { return nf.format(Math.round(n || 0)); }
function fmtCompact(n) {
  n = n || 0;
  if (Math.abs(n) >= 1000000) return nf1.format(n / 1000000) + ' M';
  if (Math.abs(n) >= 1000) return nf1.format(n / 1000) + ' k';
  return nf.format(Math.round(n));
}
function fmtDecimal(n) { return nf1.format(n || 0); }
function fmtDecimal2(n) { return nf2.format(n || 0); }
function fmtPercent(n) { return nf1.format((n || 0) * 100) + ' %'; }
function fmtRoas(n) { return nf2.format(n || 0) + '×'; }
function fmtDelta(d) {
  if (d == null) return '—';
  const pct = d * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${nf1.format(pct)} %`;
}
// '20260612' → '12/06'
function fmtDay(s) {
  if (!s || s.length !== 8) return s;
  return `${s.slice(6, 8)}/${s.slice(4, 6)}`;
}

// Formatteur monétaire dépendant de la devise du compte.
function makeMoney(currency) {
  let fmt;
  try {
    fmt = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency || 'EUR', maximumFractionDigits: 0 });
  } catch {
    fmt = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
  }
  let fmt2;
  try {
    fmt2 = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency || 'EUR', maximumFractionDigits: 2 });
  } catch {
    fmt2 = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
  }
  return {
    money: (n) => fmt.format(n || 0),
    money2: (n) => fmt2.format(n || 0),
  };
}

const PALETTE = ['#0891b2', '#3b82f6', '#f59e0b', '#22c55e', '#a855f7', '#ef4444', '#14b8a6', '#ec4899', '#64748b', '#84cc16'];

const LS_CUSTOMER = 'crm-gads-customer';
const LS_PRESET = 'crm-gads-preset';

// ── Composants atomiques ─────────────────────────────────────────────────────

function KpiCard({ def, kpi, chartData }) {
  if (!kpi) return null;
  const positive = def.lowerIsBetter ? (kpi.delta != null && kpi.delta < 0) : (kpi.delta != null && kpi.delta > 0);
  const negative = def.lowerIsBetter ? (kpi.delta != null && kpi.delta > 0) : (kpi.delta != null && kpi.delta < 0);
  const deltaClass = kpi.delta == null ? 'flat' : positive ? 'up' : negative ? 'down' : 'flat';
  const spark = def.series && chartData && chartData.length > 1
    ? chartData.map(p => ({ v: p[def.series] || 0 })) : null;
  return (
    <div className="gads-kpi">
      <div className="gads-kpi-label">{def.label}</div>
      <div className="gads-kpi-value">{def.fmt(kpi.value)}</div>
      <div className="gads-kpi-foot">
        <span className={`gads-delta ${deltaClass}`}>
          {kpi.delta != null && (positive ? '▲' : negative ? '▼' : '')} {fmtDelta(kpi.delta)}
        </span>
        {spark && (
          <span className="gads-spark">
            <ResponsiveContainer width="100%" height={28}>
              <AreaChart data={spark} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                <Area type="monotone" dataKey="v" stroke="var(--color-accent)" fill="var(--color-accent-light)" strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </span>
        )}
      </div>
    </div>
  );
}

function Panel({ title, subtitle, children, className = '' }) {
  return (
    <div className={`gads-panel ${className}`}>
      <div className="gads-panel-head">
        <h3>{title}</h3>
        {subtitle && <span className="gads-panel-sub">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function BreakdownTable({ rows, cols, empty = 'Aucune donnée' }) {
  if (!rows || rows.length === 0) return <div className="gads-empty">{empty}</div>;
  return (
    <table className="gads-table">
      <thead>
        <tr>
          <th>{cols.labelHead || ''}</th>
          {cols.metrics.map(m => <th key={m.key} className="num">{m.head}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td className="gads-td-label" title={r.label}>{r.label}</td>
            {cols.metrics.map(m => <td key={m.key} className="num">{m.fmt(r[m.key])}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DonutChart({ data, dataKey, valueFmt = fmtNumber, height = 240 }) {
  const total = (data || []).reduce((s, d) => s + (d[dataKey] || 0), 0);
  if (!data || data.length === 0) return <div className="gads-empty">Aucune donnée</div>;
  return (
    <div className="gads-donut-wrap">
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} dataKey={dataKey} nameKey="label" cx="50%" cy="50%"
            innerRadius={55} outerRadius={85} paddingAngle={2} stroke="var(--bg-surface)" strokeWidth={2}>
            {data.map((d, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip content={<DonutTooltip total={total} valueFmt={valueFmt} />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="gads-legend">
        {data.slice(0, 8).map((d, i) => (
          <div className="gads-legend-row" key={i}>
            <span className="gads-legend-dot" style={{ background: PALETTE[i % PALETTE.length] }} />
            <span className="gads-legend-label" title={d.label}>{d.label}</span>
            <span className="gads-legend-val">{total ? Math.round((d[dataKey] / total) * 100) : 0} %</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DonutTooltip({ active, payload, total, valueFmt }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <div className="gads-tooltip">
      <div className="gads-tt-title">{p.name}</div>
      <div>{valueFmt(p.value)} ({total ? Math.round((p.value / total) * 100) : 0} %)</div>
    </div>
  );
}

function MainChartTooltip({ active, payload, label, metricLabel, valueFmt, compare, actionsByDate }) {
  if (!active || !payload || !payload.length) return null;
  const cur = payload.find(p => p.dataKey === 'value');
  const prev = payload.find(p => p.dataKey === 'prev');
  return (
    <div className="gads-tooltip">
      <div className="gads-tt-title">{fmtDay(label)}</div>
      <div><span className="gads-tt-dot" style={{ background: 'var(--color-accent)' }} />{metricLabel} : <b>{valueFmt(cur?.value)}</b></div>
      {compare && prev && (
        <div className="muted"><span className="gads-tt-dot" style={{ background: 'var(--text-muted)' }} />Période préc. : {valueFmt(prev.value)}</div>
      )}
      <ActionTooltipRows items={actionsByDate && actionsByDate[label]} />
    </div>
  );
}

// ── Onglet Audit IA ──────────────────────────────────────────────────────────

function scoreColor(s) {
  if (s == null || Number.isNaN(s)) return 'var(--text-muted)';
  if (s >= 70) return 'var(--color-success)';
  if (s >= 45) return 'var(--color-warning)';
  return 'var(--color-error)';
}
const DIM_LABELS = {
  tracking: 'Mesure & conversions', structure: 'Structure du compte', bidding: 'Budgets & enchères',
  keywords: 'Mots-clés', search_terms: 'Termes & négatifs', ads: 'Annonces & extensions',
  coverage: "Part d'impressions", targeting: 'Ciblage géo/appareils', waste: 'Gaspillage',
};
const PRIO = {
  P1: { label: 'Prioritaire', cls: 'gads-prio-p1' },
  P2: { label: 'Important', cls: 'gads-prio-p2' },
  P3: { label: 'Optimisation', cls: 'gads-prio-p3' },
};
const EFFORT_CLS = { faible: 'gads-eff-low', moyen: 'gads-eff-mid', 'élevé': 'gads-eff-high', 'élevée': 'gads-eff-high' };

function AuditTab({ customer, accountName, money }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchAudit = useCallback(async () => {
    if (!customer) return null;
    try {
      const { data } = await adsApi.getAudit(customer);
      setAudit(data);
      return data;
    } catch (e) { setError(e.response?.data?.error || e.message); return null; }
  }, [customer]);

  const startPolling = useCallback(() => {
    setRunning(true);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const d = await fetchAudit();
      if (d && d.status !== 'running') { clearInterval(pollRef.current); setRunning(false); }
    }, 5000);
  }, [fetchAudit]);

  // Charge le dernier audit au changement de compte ; reprend le polling si en cours.
  useEffect(() => {
    setAudit(null); setError(null); setRunning(false); setLoading(true);
    fetchAudit().then(d => {
      setLoading(false);
      if (d && d.status === 'running') startPolling();
    });
    return () => clearInterval(pollRef.current);
  }, [customer, fetchAudit, startPolling]);

  const launch = async () => {
    setError(null); setRunning(true);
    try { await adsApi.runAudit(customer); startPolling(); }
    catch (e) { setError(e.response?.data?.error || e.message); setRunning(false); }
  };

  if (!customer) return <div className="gads-loading">Sélectionnez un compte.</div>;
  if (loading) return <div className="gads-loading">Chargement de l'audit…</div>;

  const done = audit && audit.status === 'done';
  const isRunning = running || (audit && audit.status === 'running');
  const recos = (done && audit.recommendations) || [];
  const quickWins = recos.filter(r => r.is_quick_win);
  const scores = (done && audit.scores) || [];

  return (
    <div className="gads-audit">
      <div className="gads-audit-head">
        <div>
          <h3>🤖 Audit IA de la configuration</h3>
          <p className="muted">Analyse complète par l'IA · 30 derniers jours · {accountName || 'compte'} · lecture seule (aucune modification appliquée)</p>
        </div>
        <button className="page-action-btn" onClick={launch} disabled={isRunning}>
          {isRunning ? '⏳ Audit en cours…' : (done ? "↻ Relancer l'audit" : "▶ Lancer l'audit")}
        </button>
      </div>

      {error && <div className="gads-error">⚠️ {error}</div>}
      {isRunning && <div className="gads-audit-running">L'IA analyse votre compte (collecte des données + synthèse)… ~1 minute. Vous pouvez quitter et revenir.</div>}
      {audit && audit.status === 'error' && !isRunning && (
        <div className="gads-error">L'audit a échoué : {audit.error || 'erreur inconnue'}. Réessayez.</div>
      )}
      {!audit && !isRunning && (
        <div className="gads-audit-empty">
          <p>Aucun audit pour ce compte. Lancez une analyse complète — structure, suivi des conversions, enchères, budgets, mots-clés, gaspillage, couverture, ciblage — avec des recommandations priorisées et chiffrées.</p>
        </div>
      )}

      {done && (
        <>
          <div className="gads-audit-summary">
            <div className="gads-audit-score" style={{ borderColor: scoreColor(Number(audit.global_score)) }}>
              <div className="gads-audit-score-val" style={{ color: scoreColor(Number(audit.global_score)) }}>{Math.round(Number(audit.global_score))}</div>
              <div className="gads-audit-score-max">/ 100</div>
            </div>
            <div className="gads-audit-verdict">
              <p>{audit.verdict}</p>
              {Number(audit.wasted_spend) > 0 && (
                <div className="gads-audit-wasted">💸 Budget gaspillé estimé : <strong>{money(Number(audit.wasted_spend))}</strong> / mois</div>
              )}
              <div className="muted gads-audit-date">Audit du {new Date(audit.run_at).toLocaleString('fr-FR')} · {audit.model}</div>
            </div>
          </div>

          {quickWins.length > 0 && (
            <Panel title="⚡ Quick wins" subtitle="Fort impact, faible effort — à faire en premier">
              <div className="gads-qw-grid">
                {quickWins.map((r, i) => (
                  <div key={i} className="gads-qw-card">
                    <strong>{r.title}</strong>
                    <span className="muted">{r.action}</span>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          <Panel title="Scores par dimension">
            <div className="gads-dim-grid">
              {scores.map((s, i) => (
                <div key={i} className="gads-dim">
                  <div className="gads-dim-top">
                    <span>{DIM_LABELS[s.dimension] || s.dimension}</span>
                    <strong style={{ color: scoreColor(Number(s.score)) }}>{Math.round(Number(s.score))}</strong>
                  </div>
                  <div className="gads-dim-bar"><div style={{ width: `${Math.max(0, Math.min(100, Number(s.score)))}%`, background: scoreColor(Number(s.score)) }} /></div>
                  <p className="muted">{s.summary}</p>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Recommandations" subtitle={`${recos.length} action(s) classée(s) par priorité`}>
            <div className="gads-reco-list">
              {recos.map((r, i) => {
                const prio = PRIO[r.priority] || { label: r.priority, cls: 'gads-prio-p3' };
                return (
                  <div key={i} className="gads-reco">
                    <div className="gads-reco-head">
                      <span className={`gads-prio ${prio.cls}`}>{prio.label}</span>
                      <strong>{r.title}</strong>
                      {r.is_quick_win && <span className="gads-qw-tag">⚡ quick win</span>}
                      {r.effort && <span className={`gads-eff ${EFFORT_CLS[String(r.effort).toLowerCase()] || ''}`}>effort {r.effort}</span>}
                    </div>
                    {r.finding && <p className="gads-reco-finding">📊 {r.finding}</p>}
                    {r.why && <p className="gads-reco-why">{r.why}</p>}
                    {r.action && <p className="gads-reco-action">✅ <strong>À faire :</strong> {r.action}</p>}
                    {r.impact && <p className="gads-reco-impact">📈 Impact estimé : {r.impact}</p>}
                  </div>
                );
              })}
            </div>
          </Panel>

          {audit.report_md && (
            <Panel title="Rapport détaillé">
              <details className="gads-report">
                <summary>Afficher le rapport complet</summary>
                <div className="gads-report-md">{audit.report_md}</div>
              </details>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}

// ── Onglet Pilote auto ───────────────────────────────────────────────────────

const ACTION_TYPE_LABELS = {
  ADD_NEGATIVE: { icon: '🚫', label: 'Exclure une recherche' },
  PAUSE_KEYWORD: { icon: '⏸️', label: 'Pause mot-clé' },
  ADJUST_BUDGET: { icon: '💶', label: 'Ajuster le budget' },
  FIX_GEO: { icon: '📍', label: 'Ciblage géo' },
  ADD_KEYWORD: { icon: '➕', label: 'Ajouter un mot-clé' },
};
const AP_STATUS = { proposed: 'À valider', applied: 'Appliquée', rejected: 'Rejetée', reverted: 'Annulée', failed: 'Échec' };

function AutopilotTab({ customer, accountName, user }) {
  const isSuperAdmin = user?.role === 'super-admin';
  const [config, setConfig] = useState(null);
  const [actions, setActions] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    if (!customer) return;
    setLoading(true); setError(null);
    try {
      const [c, a, sch, rn] = await Promise.all([
        adsApi.getAutopilotConfig(customer), adsApi.getActions(customer),
        adsApi.getScheduled(customer), adsApi.getRuns(customer),
      ]);
      setConfig(c.data); setActions(a.data); setScheduled(sch.data); setRuns(rn.data);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [customer]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true); setError(null);
    try {
      const { data } = await adsApi.generateActions(customer);
      await load();
      if (data.created === 0) setError(data.candidates === 0
        ? 'Aucune action à proposer — le compte est déjà propre 👍'
        : 'Aucune nouvelle action (déjà proposées, ou plafond hebdomadaire atteint).');
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setGenerating(false); }
  };

  const act = async (id, fn) => {
    setBusy(b => ({ ...b, [id]: true })); setError(null);
    try { await fn(id); await load(); }
    catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setBusy(b => ({ ...b, [id]: false })); }
  };

  const saveConfig = async (patch) => {
    try { const { data } = await adsApi.saveAutopilotConfig({ customer, ...config, ...patch }); setConfig(data); }
    catch (e) { setError(e.response?.data?.error || e.message); }
  };

  // ── Actions du mode autonome ──
  const doRun = async (fn, msg) => {
    setRunBusy(true); setError(null); setInfo(null);
    try { const r = await fn(); await load(); if (msg) setInfo(typeof msg === 'function' ? msg(r?.data) : msg); }
    catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setRunBusy(false); }
  };
  const triggerRun = (dryRun) => doRun(
    () => adsApi.triggerRun(customer, dryRun),
    (d) => d?.status === 'skipped' ? `Run ignoré : ${d.skip_reason}`
      : dryRun ? `Simulation : ${d?.n_scheduled ?? 0} action(s) seraient programmée(s), ${d?.n_skipped ?? 0} écartée(s).`
      : `${d?.n_scheduled ?? 0} action(s) programmée(s) (fenêtre de veto), ${d?.n_skipped ?? 0} écartée(s).`);
  const executeNow = () => doRun(() => adsApi.executeNow(customer), 'Exécution des actions échues déclenchée.');
  const freezeBudget = () => doRun(async () => {
    const { data } = await adsApi.triggerRun(customer, true); // dry-run → renvoie le budget total observé
    const total = data?.obs?.totalDailyBudget;
    if (total == null) throw new Error('Budget total introuvable (dry-run).');
    await adsApi.saveAutopilotConfig({ customer, ...config, auto_total_daily_budget_ceiling_eur: Number(total.toFixed(2)) });
    return { data: { total } };
  }, (d) => `Budget gelé au total actuel : ${Number(d?.total || 0).toFixed(2)} €/j.`);

  const toggleAutoType = (type) => {
    const cur = new Set(config.auto_allowed_types || []);
    cur.has(type) ? cur.delete(type) : cur.add(type);
    saveConfig({ auto_allowed_types: Array.from(cur) });
  };

  if (!customer) return <div className="gads-loading">Sélectionnez un compte.</div>;

  const pending = actions.filter(a => a.status === 'proposed');
  const history = actions.filter(a => a.status !== 'proposed');
  const approveAll = async () => { for (const a of pending) { await act(a.id, adsApi.applyAction); } };
  const autoOn = config?.mode === 'auto';
  const hasAutoApplied = history.some(a => a.status === 'applied');

  return (
    <div className="gads-audit">
      <div className="gads-audit-head">
        <div>
          <h3>🚀 Pilote auto</h3>
          <p className="muted">Mode <strong>{autoOn ? 'AUTONOME' : 'suggestion'}</strong> · {accountName || 'compte'} · {autoOn
            ? <>l'IA décide seule et <strong>programme</strong> les changements ; ils s'appliquent après la fenêtre de veto, sauf annulation.</>
            : <>l'IA propose, <strong>tu valides en 1 clic</strong>.</>}</p>
        </div>
        <div className="gads-ap-head-actions">
          <GoogleAdsChat customer={customer} accountName={accountName} onActionsChanged={load} />
          <button className="page-action-btn" onClick={generate} disabled={generating}>
            {generating ? '⏳ Analyse…' : '🔄 Générer les actions'}
          </button>
        </div>
      </div>

      {error && <div className="gads-error">⚠️ {error}</div>}
      {info && <div className="gads-info" style={{ background: 'var(--color-info-light)', color: 'var(--color-info)', padding: '8px 12px', borderRadius: 8, margin: '8px 0' }}>ℹ️ {info}</div>}
      {loading && <div className="gads-loading">Chargement…</div>}

      {/* ── Mode AUTONOME (super-admin) ── */}
      {config && isSuperAdmin && (
        <div className="gads-ap-auto" style={{ border: `2px solid ${autoOn ? 'var(--color-warning)' : 'var(--border-default)'}`, borderRadius: 12, padding: 16, margin: '12px 0', background: 'var(--bg-surface)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <strong>🤖 Mode autonome (fenêtre de veto)</strong>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label className="gads-ap-toggle" style={{ margin: 0 }}>
                Mode&nbsp;
                <select value={config.mode || 'suggest'} onChange={e => saveConfig({ mode: e.target.value, enabled: e.target.value === 'auto' ? true : config.enabled })}>
                  <option value="suggest">suggestion</option>
                  <option value="auto">autonome</option>
                </select>
              </label>
              <label className="gads-ap-toggle" style={{ margin: 0, color: config.kill_switch ? 'var(--color-error)' : 'inherit', fontWeight: config.kill_switch ? 700 : 400 }}>
                <input type="checkbox" checked={!!config.kill_switch} onChange={e => saveConfig({ kill_switch: e.target.checked })} />
                🛑 Kill switch
              </label>
            </div>
          </div>

          {autoOn && (
            <>
              <p className="muted" style={{ margin: '8px 0' }}>⚠️ <strong>Compte réel — dépense réelle.</strong> Rien ne s'applique avant la fin de la fenêtre de veto. Requiert aussi l'allowlist serveur (<code>GOOGLE_ADS_AUTOPILOT_ALLOWLIST</code>) et le kill global désactivé.</p>

              <div className="gads-ap-limits" style={{ marginTop: 8 }}>
                <label>Fenêtre de veto (heures)<input type="number" value={config.auto_veto_hours ?? 12} onChange={e => setConfig({ ...config, auto_veto_hours: e.target.value })} onBlur={e => saveConfig({ auto_veto_hours: Number(e.target.value) })} /></label>
                <label>Actions max / run<input type="number" value={config.auto_max_actions_per_run ?? 3} onChange={e => setConfig({ ...config, auto_max_actions_per_run: e.target.value })} onBlur={e => saveConfig({ auto_max_actions_per_run: Number(e.target.value) })} /></label>
                <label>Actions max / jour<input type="number" value={config.auto_max_actions_per_day ?? 8} onChange={e => setConfig({ ...config, auto_max_actions_per_day: e.target.value })} onBlur={e => saveConfig({ auto_max_actions_per_day: Number(e.target.value) })} /></label>
                <label>Cooldown / cible (heures)<input type="number" value={config.auto_cooldown_hours ?? 48} onChange={e => setConfig({ ...config, auto_cooldown_hours: e.target.value })} onBlur={e => saveConfig({ auto_cooldown_hours: Number(e.target.value) })} /></label>
                <label>Plafond dépense mensuelle (€)<input type="number" value={config.auto_monthly_spend_cap_eur ?? ''} onChange={e => setConfig({ ...config, auto_monthly_spend_cap_eur: e.target.value })} onBlur={e => saveConfig({ auto_monthly_spend_cap_eur: e.target.value === '' ? null : Number(e.target.value) })} /></label>
                <label>Budget total gelé (€/j)<input type="number" value={config.auto_total_daily_budget_ceiling_eur ?? ''} readOnly title="Utilise « Geler le budget actuel »" /></label>
              </div>

              <div style={{ margin: '10px 0' }}>
                <span className="muted">Leviers autorisés en auto (réversibles) :</span>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
                  {Object.entries(ACTION_TYPE_LABELS).map(([type, t]) => (
                    <label key={type} className="gads-ap-toggle" style={{ margin: 0 }}>
                      <input type="checkbox" checked={(config.auto_allowed_types || []).includes(type)} onChange={() => toggleAutoType(type)} />
                      {t.icon} {t.label}
                    </label>
                  ))}
                </div>
              </div>

              <label className="gads-ap-toggle" style={{ display: 'flex', margin: '8px 0' }}>
                <input type="checkbox" checked={!!config.auto_use_planner} onChange={e => saveConfig({ auto_use_planner: e.target.checked })} />
                🧠 Planner IA — traduire aussi les recommandations du dernier audit en actions (dans la limite des plafonds)
              </label>

              <label style={{ display: 'block', margin: '8px 0' }}>Emails du digest quotidien (séparés par des virgules)
                <input type="text" style={{ width: '100%' }} defaultValue={(config.digest_emails || []).join(', ')}
                  onBlur={e => saveConfig({ digest_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
              </label>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                <button className="gads-btn-approve" disabled={runBusy} onClick={() => triggerRun(true)}>▶ Simuler (dry-run)</button>
                <button className="page-action-btn" disabled={runBusy} onClick={() => triggerRun(false)}>▶ Planifier maintenant</button>
                <button className="gads-btn-approve" disabled={runBusy} onClick={executeNow}>⏩ Exécuter les dus</button>
                <button className="gads-btn-reject" disabled={runBusy} onClick={freezeBudget}>🔒 Geler le budget actuel</button>
                {hasAutoApplied && <button className="gads-btn-reject" disabled={runBusy} onClick={() => doRun(() => adsApi.rollbackAllAuto(customer), 'Annulation de toutes les actions autonomes.')}>↩↩ Annuler toutes les actions autonomes</button>}
              </div>
              <p className="muted gads-ap-note" style={{ marginTop: 8 }}>Toujours exclus de l'automatique : stratégie d'enchères, mise en pause de campagne, tracking de conversion.</p>
            </>
          )}
        </div>
      )}

      {config && (
        <details className="gads-ap-config">
          <summary>⚙️ Réglages mode suggestion & garde-fous communs</summary>
          <div className="gads-ap-config-body">
            <label className="gads-ap-toggle">
              <input type="checkbox" checked={!!config.enabled} onChange={e => saveConfig({ enabled: e.target.checked })} />
              Inclure ce compte dans la routine automatique (hebdo en suggestion, quotidienne en autonome)
            </label>
            <div className="gads-ap-limits">
              <label>Budget : ajustement max (%)<input type="number" value={config.max_budget_delta_pct} onChange={e => setConfig({ ...config, max_budget_delta_pct: e.target.value })} onBlur={e => saveConfig({ max_budget_delta_pct: Number(e.target.value) })} /></label>
              <label>Actions max / semaine<input type="number" value={config.max_actions_per_week} onChange={e => setConfig({ ...config, max_actions_per_week: e.target.value })} onBlur={e => saveConfig({ max_actions_per_week: Number(e.target.value) })} /></label>
              <label>Pause mot-clé : clics min<input type="number" value={config.pause_min_clicks} onChange={e => setConfig({ ...config, pause_min_clicks: e.target.value })} onBlur={e => saveConfig({ pause_min_clicks: Number(e.target.value) })} /></label>
            </div>
            <p className="muted gads-ap-note">Toujours exclus de l'automatique : stratégie d'enchères, mise en pause de campagne, tracking de conversion.</p>
          </div>
        </details>
      )}

      {/* ── Actions programmées (fenêtre de veto) ── */}
      {scheduled.length > 0 && (
        <Panel title={`⏳ Programmé — fenêtre de veto (${scheduled.length})`} subtitle="S'appliqueront automatiquement à l'échéance, sauf veto">
          <div className="gads-ap-bulk"><button className="gads-btn-reject" onClick={() => doRun(() => adsApi.vetoAll(customer), 'Toutes les actions programmées ont été vetoées.')}>✕ Tout vetoer</button></div>
          <div className="gads-reco-list">
            {scheduled.map(a => {
              const t = ACTION_TYPE_LABELS[a.type] || { icon: '•', label: a.type };
              const when = a.scheduled_apply_at ? new Date(a.scheduled_apply_at) : null;
              return (
                <div key={a.id} className="gads-reco gads-ap-action">
                  <div className="gads-reco-head">
                    <span className="gads-ap-type">{t.icon} {t.label}</span>
                    <strong>{a.title}</strong>
                    {a.impact && <span className="gads-ap-impact">{a.impact}</span>}
                  </div>
                  <p className="gads-reco-why">{a.rationale}</p>
                  <div className="gads-ap-buttons">
                    <span className="muted">🕒 s'applique le {when ? when.toLocaleString('fr-FR') : '—'}</span>
                    <button className="gads-btn-reject" disabled={busy[a.id]} onClick={() => act(a.id, adsApi.veto)}>✕ Vetoer</button>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      <Panel title={`⏳ À valider (${pending.length})`} subtitle={pending.length ? 'Approuve ou rejette chaque action' : null}>
        {pending.length === 0 ? (
          <div className="gads-empty">Aucune action en attente. Clique sur « Générer les actions » pour analyser le compte.</div>
        ) : (
          <>
            <div className="gads-ap-bulk"><button className="gads-btn-approve" onClick={approveAll}>✓ Tout approuver</button></div>
            <div className="gads-reco-list">
              {pending.map(a => {
                const t = ACTION_TYPE_LABELS[a.type] || { icon: '•', label: a.type };
                return (
                  <div key={a.id} className="gads-reco gads-ap-action">
                    <div className="gads-reco-head">
                      <span className="gads-ap-type">{t.icon} {t.label}</span>
                      <strong>{a.title}</strong>
                      {a.impact && <span className="gads-ap-impact">{a.impact}</span>}
                    </div>
                    <p className="gads-reco-why">{a.rationale}</p>
                    <div className="gads-ap-buttons">
                      <button className="gads-btn-approve" disabled={busy[a.id]} onClick={() => act(a.id, adsApi.applyAction)}>{busy[a.id] ? '…' : '✓ Approuver & appliquer'}</button>
                      <button className="gads-btn-reject" disabled={busy[a.id]} onClick={() => act(a.id, adsApi.rejectAction)}>✕ Rejeter</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Panel>

      {history.length > 0 && (
        <Panel title="Historique">
          <table className="gads-table gads-ap-history">
            <tbody>
              {history.map(a => (
                <tr key={a.id}>
                  <td>{(ACTION_TYPE_LABELS[a.type] || {}).icon} {a.title}</td>
                  <td><span className={`gads-ap-status st-${a.status}`}>{AP_STATUS[a.status] || a.status}</span></td>
                  <td className="num">
                    {a.status === 'applied' && a.revertable && (
                      <button className="gads-btn-reject" disabled={busy[a.id]} onClick={() => act(a.id, adsApi.revertAction)}>↩ Annuler</button>
                    )}
                    {a.status === 'failed' && a.error && <span className="muted" title={a.error}>⚠️</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {isSuperAdmin && runs.length > 0 && (
        <Panel title="🤖 Historique des runs autonomes">
          <table className="gads-table gads-ap-history">
            <thead><tr><th>Date</th><th>Phase</th><th>Statut</th><th className="num">Prog.</th><th className="num">Appl.</th><th className="num">Écart.</th><th></th></tr></thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id}>
                  <td>{new Date(r.started_at).toLocaleString('fr-FR')}</td>
                  <td>{r.phase === 'plan' ? '📋 plan' : '⏩ exec'} <span className="muted">({r.trigger})</span></td>
                  <td><span className={`gads-ap-status st-${r.status === 'done' ? 'applied' : r.status === 'error' ? 'failed' : 'proposed'}`}>{r.status}</span>{r.skip_reason && <span className="muted" title={r.skip_reason}> ℹ️</span>}</td>
                  <td className="num">{r.n_scheduled || 0}</td>
                  <td className="num">{r.n_applied || 0}</td>
                  <td className="num">{r.n_skipped || 0}</td>
                  <td className="num">{r.n_applied > 0 && <button className="gads-btn-reject" disabled={runBusy} onClick={() => doRun(() => adsApi.rollbackRun(r.id), 'Actions de ce run annulées.')}>↩ Annuler ce run</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}

// ── Composant principal ──────────────────────────────────────────────────────

// Onglet Configuration (super-admin) : affecte chaque compte Google Ads à une concession.
function AdsConfigTab() {
  const [accounts, setAccounts] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [c, conc] = await Promise.all([adsApi.getConfig(), concessionsApi.getAll()]);
      setAccounts(c.data.accounts || []);
      setConcessions(conc.data.concessions || []);
    } catch (e) { setError(e.response?.data?.error || 'Erreur de chargement.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const save = async (id, concession_id) => {
    try {
      await adsApi.setAccountConcession(id, concession_id || null);
      setAccounts((prev) => prev.map((a) => (a.id === id ? { ...a, concession_id: concession_id ? Number(concession_id) : null } : a)));
    } catch (e) { setError(e.response?.data?.error || 'Enregistrement impossible.'); }
  };

  if (loading) return <div className="gads-empty">Chargement…</div>;

  return (
    <div className="gads-config">
      <p className="muted gads-config-hint">
        Affectez chaque compte Google Ads à une concession pour gérer les droits. Un utilisateur non transverse
        ne voit que les comptes de <strong>sa</strong> concession ; les comptes non affectés restent visibles des admins/super-admins.
        L'écriture (Pilote auto) reste réservée aux super-admins.
      </p>
      {error && <div className="status-error gads-config-err">{error}</div>}
      <div className="table-container">
        <table className="gads-table">
          <thead><tr><th>Compte Google Ads</th><th>ID client</th><th>Concession rattachée</th></tr></thead>
          <tbody>
            {accounts.length === 0 && <tr><td colSpan={3} className="gads-empty">Aucun compte.</td></tr>}
            {accounts.map((a) => (
              <tr key={a.id}>
                <td><strong>{a.name}</strong></td>
                <td className="muted">{a.id}</td>
                <td>
                  <select className="page-select" value={a.concession_id || ''} onChange={(e) => save(a.id, e.target.value)}>
                    <option value="">— Non affecté —</option>
                    {concessions.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GoogleAds({ user, setHeaderAction }) {
  const isSuperAdmin = user?.role === 'super-admin';
  const [accounts, setAccounts] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [accountsError, setAccountsError] = useState(null);

  const [customer, setCustomer] = useState(() => localStorage.getItem(LS_CUSTOMER) || '');
  const [preset, setPreset] = useState(() => localStorage.getItem(LS_PRESET) || '28j');
  const [custom, setCustom] = useState(() => computeRange('28j'));
  const [compare, setCompare] = useState(true);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [tab, setTab] = useState('overview');
  const [mainMetric, setMainMetric] = useState('cost');

  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const range = useMemo(
    () => (preset === 'custom' ? custom : computeRange(preset)),
    [preset, custom]
  );

  // Devise du compte sélectionné (pour le formatage monétaire).
  const currency = useMemo(() => {
    for (const a of accounts) for (const p of a.properties) if (p.id === customer) return p.currency || 'EUR';
    return 'EUR';
  }, [accounts, customer]);
  const { money, money2 } = useMemo(() => makeMoney(currency), [currency]);

  // Définition des KPIs (dépend du formatteur monétaire).
  const KPI_DEFS = useMemo(() => [
    { key: 'impressions',      label: 'Impressions',         fmt: fmtNumber,  series: 'impressions' },
    { key: 'clicks',           label: 'Clics',               fmt: fmtNumber,  series: 'clicks' },
    { key: 'ctr',              label: 'CTR',                 fmt: fmtPercent },
    { key: 'avgCpc',           label: 'CPC moyen',           fmt: money2,     lowerIsBetter: true },
    { key: 'cost',             label: 'Coût',                fmt: money,      series: 'cost' },
    { key: 'conversions',      label: 'Conversions',         fmt: fmtDecimal, series: 'conversions' },
    { key: 'convRate',         label: 'Taux de conversion',  fmt: fmtPercent },
    { key: 'costPerConv',      label: 'Coût / conversion',   fmt: money2,     lowerIsBetter: true },
    { key: 'conversionsValue', label: 'Valeur de conv.',     fmt: money },
    { key: 'roas',             label: 'ROAS',                fmt: fmtRoas },
  ], [money, money2]);

  const MAIN_METRICS = useMemo(() => [
    { key: 'cost',        label: 'Coût',        color: '#0891b2', fmt: money },
    { key: 'clicks',      label: 'Clics',       color: '#3b82f6', fmt: fmtNumber },
    { key: 'impressions', label: 'Impressions', color: '#f59e0b', fmt: fmtNumber },
    { key: 'conversions', label: 'Conversions', color: '#22c55e', fmt: fmtDecimal },
  ], [money]);

  // Aucune action de header pour cette page (lecture seule).
  useEffect(() => { if (setHeaderAction) setHeaderAction(null); }, [setHeaderAction]);

  // ── Chargement des comptes ──
  useEffect(() => {
    let alive = true;
    adsApi.getAccounts()
      .then(({ data }) => {
        if (!alive) return;
        setConfigured(data.configured);
        setAccounts(data.accounts || []);
        const all = (data.accounts || []).flatMap(a => a.properties);
        if (all.length && (!customer || !all.some(p => p.id === customer))) {
          setCustomer(all[0].id);
        }
      })
      .catch(err => alive && setAccountsError(err.response?.data?.error || err.message));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Chargement du rapport ──
  const loadReport = useCallback(() => {
    if (!customer) return;
    setLoading(true);
    setError(null);
    adsApi.getReport({ customer, start: range.start, end: range.end, compare })
      .then(({ data }) => setReport(data))
      .catch(err => { setError(err.response?.data?.error || err.message); setReport(null); })
      .finally(() => setLoading(false));
  }, [customer, range.start, range.end, compare]);

  useEffect(() => { if (tab !== 'accounts' && tab !== 'ai-audit' && tab !== 'autopilot' && tab !== 'config') loadReport(); }, [loadReport, tab]);

  // ── Chargement de la vue consolidée (onglet Comptes) ──
  useEffect(() => {
    if (tab !== 'accounts') return;
    setOverviewLoading(true);
    adsApi.getOverview({ start: range.start, end: range.end })
      .then(({ data }) => setOverview(data))
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setOverviewLoading(false));
  }, [tab, range.start, range.end]);

  // ── Persistance des choix ──
  useEffect(() => { if (customer) localStorage.setItem(LS_CUSTOMER, customer); }, [customer]);
  useEffect(() => { localStorage.setItem(LS_PRESET, preset); }, [preset]);

  // ── Données dérivées pour le graphe principal ──
  const mainChartData = useMemo(() => {
    if (!report?.timeseries) return [];
    const prev = report.previousTimeseries || [];
    return report.timeseries.map((p, i) => ({
      date: p.date,
      value: p[mainMetric] || 0,
      prev: prev[i] ? (prev[i][mainMetric] || 0) : null,
    }));
  }, [report, mainMetric]);

  const currentAccountName = useMemo(() => {
    for (const a of accounts) for (const p of a.properties) if (p.id === customer) return p.name;
    return '';
  }, [accounts, customer]);

  const metricDef = MAIN_METRICS.find(m => m.key === mainMetric) || MAIN_METRICS[0];
  const [actionsRefresh, setActionsRefresh] = useState(0);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const { actions: siteActions, siteId: actionSiteId, siteName: actionSiteName } =
    useSiteActions('ads', customer, range.start, range.end, !!customer, actionsRefresh);
  const isConfigTab = tab === 'ai-audit' || tab === 'autopilot' || tab === 'sea' || tab === 'config'; // onglets sans période/rapport

  // ── Rendu ──
  if (configured === false) {
    return (
      <div className="page-container">
        <div className="gads-config-banner">
          <h2>📈 Google Ads</h2>
          <p>Le connecteur Google Ads n'est pas encore configuré sur ce serveur.</p>
          <p className="muted">
            Renseigner les variables <code>GOOGLE_ADS_DEVELOPER_TOKEN</code>, <code>GOOGLE_ADS_CLIENT_ID</code>,
            <code> GOOGLE_ADS_CLIENT_SECRET</code>, <code>GOOGLE_ADS_REFRESH_TOKEN</code> et
            <code> GOOGLE_ADS_LOGIN_CUSTOMER_ID</code> dans le <code>.env</code> du backend, puis redémarrer.
          </p>
          <p className="muted">L'interface est prête : dès que la connexion au compte manager (MCC) est établie, les données s'afficheront ici.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container gads-page">
      {/* 1. Onglets */}
      <div className="page-tabs">
        <button className={tab === 'overview' ? 'active' : ''} onClick={() => setTab('overview')}>📊 Vue d'ensemble</button>
        <button className={tab === 'campaigns' ? 'active' : ''} onClick={() => setTab('campaigns')}>📣 Campagnes</button>
        <button className={tab === 'keywords' ? 'active' : ''} onClick={() => setTab('keywords')}>🔑 Mots-clés</button>
        <button className={tab === 'segments' ? 'active' : ''} onClick={() => setTab('segments')}>🎯 Réseaux & appareils</button>
        <button className={tab === 'ai-audit' ? 'active' : ''} onClick={() => setTab('ai-audit')}>🤖 Audit IA</button>
        <button className={tab === 'autopilot' ? 'active' : ''} onClick={() => setTab('autopilot')}>🚀 Pilote auto</button>
        <button className={tab === 'sea' ? 'active' : ''} onClick={() => setTab('sea')}>🎯 Création SEA</button>
        <button className={tab === 'accounts' ? 'active' : ''} onClick={() => setTab('accounts')}>🏢 Tous les comptes</button>
        {isSuperAdmin && <button className={tab === 'config' ? 'active' : ''} onClick={() => setTab('config')}>⚙️ Configuration<span className="tab-sa-lock" title="Visible par les super-admins uniquement">🔒</span></button>}
      </div>

      {/* 2. Toolbar — sélection compte + période */}
      <div className="page-toolbar gads-toolbar">
        {tab !== 'accounts' && tab !== 'config' && (
          <select className="page-select gads-acc-select" value={customer} onChange={e => setCustomer(e.target.value)}>
            {accounts.map(acc => (
              <optgroup key={acc.account} label={acc.accountName}>
                {acc.properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </optgroup>
            ))}
          </select>
        )}

        {!isConfigTab && (
          <div className="gads-presets">
            {PRESETS.map(p => (
              <button key={p.id}
                className={`gads-preset ${preset === p.id ? 'active' : ''}`}
                onClick={() => setPreset(p.id)}>{p.label}</button>
            ))}
          </div>
        )}

        {!isConfigTab && preset === 'custom' && (
          <div className="gads-custom-dates">
            <input type="date" className="page-select" value={custom.start} max={custom.end}
              onChange={e => setCustom(c => ({ ...c, start: e.target.value }))} />
            <span>→</span>
            <input type="date" className="page-select" value={custom.end} min={custom.start}
              onChange={e => setCustom(c => ({ ...c, end: e.target.value }))} />
          </div>
        )}

        {tab !== 'accounts' && !isConfigTab && (
          <button className={`gads-toggle ${compare ? 'active' : ''}`} onClick={() => setCompare(c => !c)}
            title="Comparer à la période précédente">⇄ Comparer</button>
        )}
        {!isConfigTab && (
          <button className="gads-icon-btn" onClick={() => (tab === 'accounts' ? setTab(tab) : loadReport())} title="Rafraîchir">↻</button>
        )}
      </div>

      {/* Bandeau contextuel */}
      <div className="gads-context">
        <span className="gads-context-site">{tab === 'accounts' ? 'Tous les comptes gérés' : (currentAccountName || '—')}</span>
        <span className="gads-context-range">
          {tab === 'ai-audit' ? '30 derniers jours · audit de configuration'
            : tab === 'autopilot' ? 'Pilote auto · actions à valider (lecture des 30 derniers jours)'
            : (
            <>
              {range.start} → {range.end}
              {compare && tab !== 'accounts' && report?.previous && <span className="muted"> · vs {report.previous.start} → {report.previous.end}</span>}
            </>
          )}
        </span>
      </div>

      {accountsError && <div className="gads-error">⚠️ {accountsError}</div>}
      {error && <div className="gads-error">⚠️ {error}</div>}
      {tab !== 'accounts' && report?.warnings?.length > 0 && (
        <div className="gads-warn">Certaines sections n'ont pas pu être chargées : {report.warnings.join(', ')}.</div>
      )}

      {loading && !report && tab !== 'accounts' && <div className="gads-loading">Chargement des données Google Ads…</div>}

      {/* ── ONGLETS LIÉS À UN COMPTE ── */}
      {tab !== 'accounts' && report && (
        <>
          {/* VUE D'ENSEMBLE */}
          {tab === 'overview' && (
            <>
              <div className="gads-kpi-grid">
                {KPI_DEFS.map(def => (
                  <KpiCard key={def.key} def={def} kpi={report.kpis[def.key]} chartData={report.timeseries} />
                ))}
              </div>

              <Panel
                title="Évolution dans le temps"
                subtitle={compare ? 'Trait plein = période courante · pointillé = période précédente' : null}
              >
                <div className="gads-metric-switch">
                  {MAIN_METRICS.map(m => (
                    <button key={m.key}
                      className={`gads-chip ${mainMetric === m.key ? 'active' : ''}`}
                      style={mainMetric === m.key ? { borderColor: m.color, color: m.color } : null}
                      onClick={() => setMainMetric(m.key)}>{m.label}</button>
                  ))}
                  <button className="gads-chip" title="Consigner une action menée sur ce site" style={{ marginLeft: 'auto' }} onClick={() => setShowQuickAction(true)}>+ Action</button>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={mainChartData} margin={{ top: 10, right: 16, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gadsMainFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={metricDef.color} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={metricDef.color} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} minTickGap={24} />
                    <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} width={48} />
                    <Tooltip content={<MainChartTooltip metricLabel={metricDef.label} valueFmt={metricDef.fmt} compare={compare} actionsByDate={groupMarkersByDate(actionMarkers(siteActions, mainChartData.map(p => p.date)))} />} />
                    {compare && (
                      <Line type="monotone" dataKey="prev" stroke="var(--text-muted)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} isAnimationActive={false} />
                    )}
                    <Area type="monotone" dataKey="value" stroke={metricDef.color} strokeWidth={2.5} fill="url(#gadsMainFill)" dot={false} isAnimationActive={false} />
                    {actionMarkers(siteActions, mainChartData.map(p => p.date)).map(m => (
                      <ReferenceLine key={m.key} x={m.x} stroke={m.color} strokeDasharray="4 4"
                        label={{ value: m.emoji, position: 'top', fontSize: 12 }} />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              </Panel>
              {showQuickAction && (
                <QuickActionModal siteId={actionSiteId} siteName={actionSiteName} defaultChannel="ads"
                  onClose={() => setShowQuickAction(false)}
                  onSaved={() => { setShowQuickAction(false); setActionsRefresh(x => x + 1); }} />
              )}

              <div className="gads-grid-2">
                <Panel title="Répartition du coût" subtitle="par type de campagne">
                  <DonutChart data={report.channels} dataKey="cost" valueFmt={money} />
                </Panel>
                <Panel title="Appareils" subtitle="par coût">
                  <DonutChart data={report.devices} dataKey="cost" valueFmt={money} />
                </Panel>
              </div>
            </>
          )}

          {/* CAMPAGNES */}
          {tab === 'campaigns' && (
            <Panel title="Campagnes" subtitle="Top 50 par coût">
              <BreakdownTable rows={report.campaigns} empty="Aucune campagne sur la période" cols={{
                labelHead: 'Campagne',
                metrics: [
                  { key: 'impressions', head: 'Impr.', fmt: fmtNumber },
                  { key: 'clicks', head: 'Clics', fmt: fmtNumber },
                  { key: 'ctr', head: 'CTR', fmt: fmtPercent },
                  { key: 'avgCpc', head: 'CPC', fmt: money2 },
                  { key: 'cost', head: 'Coût', fmt: money },
                  { key: 'conversions', head: 'Conv.', fmt: fmtDecimal },
                  { key: 'costPerConv', head: 'Coût/conv.', fmt: money2 },
                  { key: 'roas', head: 'ROAS', fmt: fmtRoas },
                ],
              }} />
            </Panel>
          )}

          {/* MOTS-CLÉS */}
          {tab === 'keywords' && (
            <>
              <Panel title="Mots-clés" subtitle="Top 25 par coût">
                <BreakdownTable rows={report.keywords} empty="Aucun mot-clé (campagnes hors Recherche ?)" cols={{
                  labelHead: 'Mot-clé',
                  metrics: [
                    { key: 'impressions', head: 'Impr.', fmt: fmtNumber },
                    { key: 'clicks', head: 'Clics', fmt: fmtNumber },
                    { key: 'ctr', head: 'CTR', fmt: fmtPercent },
                    { key: 'cost', head: 'Coût', fmt: money },
                    { key: 'conversions', head: 'Conv.', fmt: fmtDecimal },
                  ],
                }} />
              </Panel>
              <Panel title="Termes de recherche" subtitle="Top 25 par impressions — ce que tapent réellement les internautes">
                <BreakdownTable rows={report.searchTerms} empty="Aucun terme de recherche disponible" cols={{
                  labelHead: 'Terme de recherche',
                  metrics: [
                    { key: 'impressions', head: 'Impr.', fmt: fmtNumber },
                    { key: 'clicks', head: 'Clics', fmt: fmtNumber },
                    { key: 'cost', head: 'Coût', fmt: money },
                    { key: 'conversions', head: 'Conv.', fmt: fmtDecimal },
                  ],
                }} />
              </Panel>
            </>
          )}

          {/* RÉSEAUX & APPAREILS */}
          {tab === 'segments' && (
            <>
              <div className="gads-grid-2">
                <Panel title="Réseaux" subtitle="répartition du coût">
                  <DonutChart data={report.networks} dataKey="cost" valueFmt={money} />
                </Panel>
                <Panel title="Réseaux — détail">
                  <BreakdownTable rows={report.networks} cols={{
                    labelHead: 'Réseau',
                    metrics: [
                      { key: 'impressions', head: 'Impr.', fmt: fmtNumber },
                      { key: 'clicks', head: 'Clics', fmt: fmtNumber },
                      { key: 'cost', head: 'Coût', fmt: money },
                      { key: 'conversions', head: 'Conv.', fmt: fmtDecimal },
                    ],
                  }} />
                </Panel>
              </div>
              <div className="gads-grid-2">
                <Panel title="Appareils" subtitle="répartition du coût">
                  <DonutChart data={report.devices} dataKey="cost" valueFmt={money} />
                </Panel>
                <Panel title="Appareils — détail">
                  <BreakdownTable rows={report.devices} cols={{
                    labelHead: 'Appareil',
                    metrics: [
                      { key: 'impressions', head: 'Impr.', fmt: fmtNumber },
                      { key: 'clicks', head: 'Clics', fmt: fmtNumber },
                      { key: 'cost', head: 'Coût', fmt: money },
                      { key: 'conversions', head: 'Conv.', fmt: fmtDecimal },
                    ],
                  }} />
                </Panel>
              </div>
            </>
          )}
        </>
      )}

      {/* ── ONGLET AUDIT IA ── */}
      {tab === 'ai-audit' && (
        <AuditTab customer={customer} accountName={currentAccountName} money={money} />
      )}

      {/* ── ONGLET PILOTE AUTO ── */}
      {tab === 'autopilot' && (
        <AutopilotTab customer={customer} accountName={currentAccountName} user={user} />
      )}

      {tab === 'sea' && (
        <SeaCampaignBuilder customer={customer} accountName={currentAccountName} isSuperAdmin={isSuperAdmin} />
      )}

      {/* ── ONGLET CONFIGURATION (super-admin) ── */}
      {tab === 'config' && isSuperAdmin && <AdsConfigTab />}

      {/* ── ONGLET CONSOLIDÉ (TOUS LES COMPTES) ── */}
      {tab === 'accounts' && (
        <>
          {overviewLoading && !overview && <div className="gads-loading">Agrégation de tous les comptes…</div>}
          {overview?.accounts?.length > 0 && (
            <>
              <Panel title="Coût par compte" subtitle="sur la période sélectionnée">
                <ResponsiveContainer width="100%" height={Math.max(220, overview.accounts.length * 34)}>
                  <BarChart data={overview.accounts} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" horizontal={false} />
                    <XAxis type="number" tickFormatter={fmtCompact} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                    <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: 'var(--text-primary)' }} />
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="cost" fill="var(--color-accent)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title="Tous les comptes — détail" subtitle="trié par coût décroissant">
                <table className="gads-table">
                  <thead>
                    <tr>
                      <th>Compte</th>
                      <th className="num">Impr.</th>
                      <th className="num">Clics</th>
                      <th className="num">CTR</th>
                      <th className="num">Coût</th>
                      <th className="num">Conv.</th>
                      <th className="num">Coût/conv.</th>
                      <th className="num">ROAS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.accounts.map((a) => {
                      const m = makeMoney(a.currency);
                      return (
                        <tr key={a.id}>
                          <td className="gads-td-label" title={`${a.name} (${a.id})`}>
                            {a.error ? '⚠️ ' : ''}{a.name}
                          </td>
                          <td className="num">{fmtNumber(a.impressions)}</td>
                          <td className="num">{fmtNumber(a.clicks)}</td>
                          <td className="num">{fmtPercent(a.ctr)}</td>
                          <td className="num">{m.money(a.cost)}</td>
                          <td className="num">{fmtDecimal(a.conversions)}</td>
                          <td className="num">{m.money2(a.costPerConv)}</td>
                          <td className="num">{fmtRoas(a.roas)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Panel>
            </>
          )}
          {overview && overview.accounts && overview.accounts.length === 0 && (
            <div className="gads-empty">Aucun compte géré trouvé sous ce MCC.</div>
          )}
        </>
      )}
    </div>
  );
}

export default GoogleAds;
